import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HELP_SECTIONS, lookupHelp } from './helpContent.js';
import './help.scss';
import { useLanguage } from '../i18n/LanguageContext.jsx';

function HelpNav({ sections, activeId, onSelect }) {
  const { t } = useLanguage();
  return (
    <div className="help-nav" role="tablist">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          role="tab"
          aria-selected={s.id === activeId}
          className={`help-tab${s.id === activeId ? ' active' : ''}`}
          onClick={() => onSelect(s.id)}
        >
          <i className={`fas ${s.icon} me-1`} aria-hidden="true"></i>
          {t(s.title)}
        </button>
      ))}
    </div>
  );
}

function HelpContent({ section, topicId, onSelect }) {
  const { t } = useLanguage();
  const topic = section.topics.find((tp) => tp.id === topicId) || section.topics[0];
  return (
    <>
      <div className="help-pills">
        {section.topics.map((tp) => (
          <button
            key={tp.id}
            type="button"
            className={`help-pill${tp.id === topic.id ? ' active' : ''}`}
            onClick={() => onSelect(tp.id)}
          >
            {t(tp.title)}
          </button>
        ))}
      </div>
      <div className="help-body" dangerouslySetInnerHTML={{ __html: topic.html }} />
    </>
  );
}

/** `?` button + slide-in help drawer. */
export default function HelpDrawer() {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [sectionId, setSectionId] = useState('welcome');
  const [topicId, setTopicId] = useState('getting-started');

  const openHelp = () => {
    const hit = lookupHelp(pathname);
    setSectionId(hit.sectionId);
    setTopicId(hit.topicId);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const section = HELP_SECTIONS.find((s) => s.id === sectionId) || HELP_SECTIONS[0];

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-light help-trigger"
        aria-label={t('Open help')}
        onClick={openHelp}
      >
        <i className="fas fa-question" aria-hidden="true"></i>
      </button>
      {open && (
        <>
          <div
            className="help-backdrop"
            role="presentation"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          />
          <aside className="help-drawer" role="dialog" aria-modal="true" aria-label={t('Help')}>
            <div className="help-header">
              <strong>{t('Help')}</strong>
              <button
                type="button"
                className="btn-close"
                aria-label={t('Close help')}
                onClick={() => setOpen(false)}
              />
            </div>
            <HelpNav
              sections={HELP_SECTIONS}
              activeId={section.id}
              onSelect={(id) => {
                setSectionId(id);
                setTopicId(HELP_SECTIONS.find((s) => s.id === id).topics[0].id);
              }}
            />
            <HelpContent section={section} topicId={topicId} onSelect={setTopicId} />
          </aside>
        </>
      )}
    </>
  );
}
